<script setup lang="ts">
import { useTerms } from '~/api/drupal'
import { useI18n } from 'vue-i18n'

interface ESResponse {
  total: number
  hits: CbzSearchHit[]
}
interface CbzSearchHit {
  fields: {
    title: string[]
    url: string[]
    category: string[]
    type: string[]
  }
}
interface ResultItem {
  title: string
  url: string
  category: string[]
  itemType: string
  label: string
  target: string
}

const { t } = useI18n()
const props = defineProps<{
  targetGroup: string
  title: string
  introText: string
}>()

const config = useRuntimeConfig()

const { terms } = await useTerms(['cbz_course_category'])
const allCategories = ref(terms.value.cbz_course_category.map((item) => ({ value: item.id, label: item.name })))

const isSearchUsed = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const typingQuery = ref<string>('')
const searchInput = ref()

const allResults = ref<ResultItem[] | undefined>([])
const limit = 10
const categories = ref(allCategories.value)
const selectedCategories = ref<string[]>([])
const limitedResults = computed<ResultItem[] | undefined>(() => {
  return allResults.value?.slice(0, limit)
})

const search = async () => {
  isLoading.value = true
  const response = await fetch(`${config.public.frontendBaseUrl}/api/elasticsearch/search-cbz`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text: typingQuery.value,
      target_group: props.targetGroup,
      category: selectedCategories.value.length ? selectedCategories.value : undefined,
    }),
  }).then((response) => response.json())
  allResults.value = formatResponse(response)

  isLoading.value = false
}
const formatResponse = (response: ESResponse): ResultItem[] | undefined => {
  return response.total
    ? response.hits.map((result) => {
        return {
          title: result.fields.title[0] ?? '',
          url: result.fields.url[0] ?? '',
          category: result.fields.category,
          itemType: result.fields.type[0] ?? '',
          label: t('protectedPages.list.teaserTile.cta'),
          target: '_blank',
        }
      })
    : undefined
}

const searchText = async () => {
  isSearchUsed.value = true

  await search()

  // Update categories based on search results
  if (allResults.value) {
    const foundCategories = new Set(allResults.value.flatMap((item) => item.category))
    categories.value = allCategories.value.filter((category) => foundCategories.has(category.value))
  } else {
    categories.value = allCategories.value
    selectedCategories.value = []
  }
}
const keys = useMagicKeys({ target: searchInput })
const enter = computed(() => keys.enter?.value ?? false)

watch(enter, (value) => {
  if (value) {
    searchText()
  }
})

watch(selectedCategories, async () => {
  await search()
})
</script>
<template>
  <SectionSpecial :title="title" :sub-title="introText">
    <div v-animate="{ animation: 'vertical-reveal' }" class="wrapper">
      <SearchField
        ref="searchInput"
        v-model="typingQuery"
        :placeholder="t('shop.listingPage.search.placeholder')"
        @search="searchText"
      />
      <div v-if="isSearchUsed && limitedResults" class="categories">
        <Checkbox
          id="cbz-search-categories"
          v-model="selectedCategories"
          class="filter-checkbox"
          :options="categories"
          :required="false"
        />
      </div>
    </div>
  </SectionSpecial>

  <Section v-if="isSearchUsed">
    <div class="t-10 t-o-1 tl-7 tl-o-0 ds-o-1">
      <h2 v-animate="{ animation: 'vertical-reveal' }">{{ t('protectedPages.list.resultsTitle') }}</h2>
      <p v-if="isLoading">Loading...</p>
      <div v-else>
        <ul v-if="limitedResults" class="cul results-list">
          <li v-for="(item, index) in limitedResults" :key="index">
            <DownloadItem v-if="item.itemType === 'document'" :item="item" />
            <CbzLinkItem v-else :item="item" class="link-item" />
          </li>
        </ul>
        <p v-else>{{ t('protectedPages.list.noResults') }}</p>
      </div>
    </div>
  </Section>
</template>
<style scoped lang="scss">
.wrapper {
  max-width: 640px;
  .categories {
    margin-top: 20px;
  }
}

.link-item {
  border-bottom: 1px solid var(--c-line-medium);
}
</style>
